import { formatValue, humanizeAny, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";

export function buildScatterOption({ data, palette, base, xKey, yKey }: ChartBuildCtx): ChartOption {
  const xFmt = formatFromColumn(xKey, data.columns);
  const yFmt = formatFromColumn(yKey, data.columns);
  const xLabel = humanizeAny(xKey);
  const yLabel = humanizeAny(yKey);
  return {
    ...base,
    // Reserve room for BOTH x tick labels AND the x-axis name
    // ("Reply rate"). Without containLabel: true the axis name + tick
    // labels render below the visible canvas (caught in
    // eval-scatter-reply-vs-meetings: no x-axis label visible).
    grid: { left: 56, right: 24, top: 16, bottom: 48, containLabel: true },
    tooltip: {
      trigger: "item",
      formatter: (p: { value: [number, number]; data?: Record<string, unknown> }) => {
        const [vx, vy] = p.value;
        return `${xLabel}: ${formatValue(vx, xFmt)}<br/>${yLabel}: ${formatValue(vy, yFmt)}`;
      },
    },
    xAxis: {
      type: "value",
      name: xLabel,
      nameLocation: "middle",
      nameGap: 26,
      nameTextStyle: { color: "#6b7280", fontSize: 11 },
      axisLabel: { color: "#6b7280", fontSize: 11, formatter: (v: number) => formatValue(v, xFmt) },
    },
    yAxis: {
      type: "value",
      name: yLabel,
      nameLocation: "middle",
      nameGap: 44,
      nameTextStyle: { color: "#6b7280", fontSize: 11 },
      axisLabel: { color: "#6b7280", fontSize: 11, formatter: (v: number) => formatValue(v, yFmt) },
    },
    series: [
      {
        type: "scatter",
        // 9px with 65% fill + white border — dense clusters reveal
        // overlap via opacity, individual points stay crisp at print
        // sizes.
        symbolSize: 9,
        itemStyle: {
          color: palette[0],
          opacity: 0.65,
          borderColor: "#fff",
          borderWidth: 1,
        },
        // Drop rows where either axis is missing — without this,
        // a scatter with only 1 measure (xKey or yKey absent), OR
        // rows with explicit null values, end up with NaN tuples
        // (or worse, 0,0 tuples since Number(null) is 0) in the
        // series data. ECharts renders those as wandering points
        // at chart origin or a fake cluster on the y-axis.
        data: data.rows
          .filter((r) => r[xKey] != null && r[yKey] != null)
          .map((r) => [Number(r[xKey]), Number(r[yKey])])
          .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y)),
      },
    ],
  } as ChartOption;
}
